import React, { useEffect } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import {
  FlexRow,
  TableData,
  Title,
  BorderWrapper,
  PaddingWrapper,
} from 'components/styled-components';
import { Popover, Button, Intent, Tooltip, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import colors from 'utils/colors';
import { download } from 'utils/functions';
import hasPermission from 'utils/hasPermission';
import permissions from 'utils/permissions';
import map from 'lodash/map';
import { fetchSingleFile } from 'api/utils';
import { fetchQuestionnaireInstance } from 'actions/questionnaire';

const Row = ({ data, requestAproval }) => {
  const answer = data.answers.find(answer =>
    data.selected.includes(answer.answerID)
  );
  return (
    <TableRow>
      <TableData>{data.question}</TableData>
      <TableData>{data.questionType.typeName}</TableData>
      <TableData>{data.displayCategory.categoryName}</TableData>
      <TableData
        style ={requestAproval?{ color: answer && answer.autoFail ? '#DB3737' : '#15B371'}:{}}
      >
        {answer && answer.answer}
      </TableData>
      <TableData wordBreak="break-word">{data.note}</TableData>
    </TableRow>
  );
};

const QuestionnaireInfoBase = ({
  instance,
  files,
  appUser,
  fetchQuestionnaireInstance,
  vendorRequestID,
  requestAproval
}) => {
  useEffect(() => {
    fetchQuestionnaireInstance({
      vendorRequestID,
      status: 'ALL',
    });
  }, []);
  return (
    <BorderWrapper margin="1rem">
      <PaddingWrapper>
        <FlexRow margin="inherit inherit 1rem inherit" flex-wrap="wrap">
          <Title>Questionnaire Information</Title>
          <div>
            {hasPermission(appUser.permissions, [
              permissions.VIEW_QUESTIONNAIRE_ATTACHMENT,
              permissions.VIEW_OWNED_QUESTIONNAIRE_ATTACHMENT,
              permissions.VIEW_ALL_QUESTIONNAIRE_ATTACHMENT,
            ]) && (
              <Popover
                content={
                  <div style={{ padding: '1rem', overflowY: 'auto', maxHeight: '500px' }}>
                    <p>Click on a file to download it.</p>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexWrap: 'wrap',
                      }}
                    >
                      {map(files, file => (
                        <Button
                          minimal
                          text={file.displayName}
                          key={file.uploadID}
                          onClick={() =>
                            fetchSingleFile(
                              file.uploadID,
                              'QUESTIONNAIRE'
                            ).then(res => {
                              download(res, file.displayName);
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>
                }
              >
                <Button
                  minimal
                  large
                  intent={Intent.PRIMARY}
                  text={`(${files.length}) Attachment(s)`}
                />
              </Popover>
            )}
          </div>
        </FlexRow>
      </PaddingWrapper>
      <Table>
        <thead>
          <TableRow>
            <TableHead>Question</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Display Category</TableHead>
            <TableHead style={{ whiteSpace: 'nowrap' }}>
              Answer{' '}
              {requestAproval?     
               <Tooltip content="Answers in red are answers that automatically fail a questionnaire">
                <Icon icon={IconNames.INFO_SIGN} />
              </Tooltip>
              :null}
            </TableHead>
            <TableHead>Note</TableHead>
          </TableRow>
        </thead>
        <tbody>
          {map(instance, (ins, i) => (
            <Row key={i} data={ins} requestAproval={requestAproval}/>
          ))}
        </tbody>
      </Table>
    </BorderWrapper>
  );
};

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 3px;
`;

const TableRow = styled.tr`
  background: ${props => props.background || '#fff'};

  td {
    border-bottom: 1px solid ${colors.lightestGray};
  }
  &:first-child th {
    border-bottom: 1px solid ${colors.lightestGray};
  }
  &:last-child {
    border-bottom: none;
  }
`;

const TableHead = styled.th`
  text-align: left;
  color: ${colors.gray};
  margin-bottom: 0.5rem;
  font-weight: 400;
  padding: 1rem;
  border-bottom: 1px solid ${colors.lightGray};
`;

const mapStateToProps = state => ({
  files: state.utils.questionnaireFiles,
  instance: state.questionnaire.instance.result,
  appUser: state.auth.appUser,
});

const QuestionnaireInfo = connect(
  mapStateToProps,
  { fetchQuestionnaireInstance }
)(QuestionnaireInfoBase);

export default QuestionnaireInfo;
